'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type { ContactFormBlock, ContactFormFieldType } from '@impact/types';
import { CheckCircle, Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

function normaliseInputType(type: ContactFormFieldType): string {
  switch (type) {
    case 'phone':
      return 'tel';
    case 'email':
      return 'email';
    case 'url':
      return 'url';
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'time':
      return 'time';
    default:
      return 'text';
  }
}

export function ContactFormLive({ block }: { block: ContactFormBlock }) {
  const t = useTranslations('pages');
  const tCommon = useTranslations('common');
  const {
    title,
    subtitle,
    fields,
    submitLabel = t('sendMessage'),
    successMessage = t('contactSuccess'),
  } = block.data;

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitMutation = trpc.publicContactForm.submit.useMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const pageSlug =
        typeof window !== 'undefined' ? window.location.pathname.split('/').pop() || '' : '';

      await submitMutation.mutateAsync({
        pageTitle: title || '',
        pageSlug,
        formTitle: title || 'Contact Form',
        recipientEmail: block.data.recipientEmail || undefined,
        fields: fields.map((field) => ({
          label: field.label,
          value: formData[field.id] || '',
          type: field.type,
        })),
      });
      setIsSubmitted(true);
    } catch {
      setError(t('contactError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (fieldId: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  if (isSubmitted) {
    return (
      <div className="my-12 p-8 bg-muted/30 rounded-xl text-center">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <p className="text-lg text-foreground">{successMessage}</p>
      </div>
    );
  }

  return (
    <div className="my-12">
      {title && <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{title}</h2>}
      {subtitle && <p className="text-muted-foreground mb-8">{subtitle}</p>}

      <form onSubmit={handleSubmit} className="space-y-5 max-w-xl">
        {fields.map((field) => {
          const value = formData[field.id] || '';
          const isCheckbox = field.type === 'checkbox';
          return (
            <div key={field.id}>
              {!isCheckbox && (
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  <span>{field.label}</span>
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
              )}

              {field.type === 'textarea' ? (
                <textarea
                  rows={5}
                  required={field.required}
                  placeholder={field.placeholder}
                  value={value}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                />
              ) : field.type === 'select' ? (
                <select
                  required={field.required}
                  value={value}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">{field.placeholder || tCommon('selectOption')}</option>
                  {field.options?.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : field.type === 'radio' ? (
                <div className="space-y-2">
                  {(field.options ?? []).map((option) => (
                    <label key={option} className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="radio"
                        name={field.id}
                        required={field.required}
                        value={option}
                        checked={value === option}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        className="h-4 w-4 border-input text-primary focus:ring-primary/20"
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              ) : isCheckbox ? (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    required={field.required}
                    checked={value === 'true'}
                    onChange={(e) => handleChange(field.id, e.target.checked ? 'true' : 'false')}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-primary/20"
                  />
                  <span className="text-sm text-muted-foreground">
                    {field.placeholder || field.label}
                  </span>
                </label>
              ) : (
                <input
                  type={normaliseInputType(field.type)}
                  required={field.required}
                  placeholder={field.placeholder}
                  min={field.min}
                  max={field.max}
                  value={value}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              )}
              {field.helpText ? (
                <p className="mt-1 text-xs text-muted-foreground">{field.helpText}</p>
              ) : null}
            </div>
          );
        })}

        {error && <p className="text-sm text-red-500">{error}</p>}

        <button
          type="submit"
          disabled={isSubmitting}
          className={cn(
            'w-full px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium transition-colors',
            isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-primary/90',
          )}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {tCommon('sending')}
            </span>
          ) : (
            <span>{submitLabel}</span>
          )}
        </button>
      </form>
    </div>
  );
}
